import { trace } from '@opentelemetry/api';

/**
 * Télémétrie d'erreur, partagée.
 *
 * Ces trois fonctions vivaient en privé dans BackgroundService. Elles sont extraites
 * telles quelles pour que d'autres services puissent y recourir : BackgroundService
 * injecte PhotoService, donc l'inverse formerait un cycle. Aucun changement de
 * comportement, BackgroundService délègue simplement ici.
 */

/**
 * Décrit une erreur de manière exploitable pour la télémétrie.
 *
 * `String(error)` donnait « [object Object] » dès que l'erreur n'était pas une instance
 * d'Error — ce qui est le cas des HttpErrorResponse d'Angular, qui étendent
 * HttpResponseBase. On perdait donc statut, URL et message serveur, et les erreurs de
 * synchronisation photo arrivaient dans OpenObserve sans aucune cause exploitable.
 * Diagnostiquer un blocage terrain imposait alors de repasser par la base.
 */
export function decrireErreur(error: any): { message: string; attributs: Record<string, string | number> } {
    const attributs: Record<string, string | number> = {};

    if (error instanceof Error) {
        attributs['error.name'] = error.name;
        return { message: error.message || error.name, attributs };
    }

    if (error && typeof error === 'object') {
        const statut = (error as any).status;

        // HttpErrorResponse et assimilés : c'est là que se trouve l'information utile.
        if (statut !== undefined) {
            attributs['http.status_code'] = statut;
            const url = (error as any).url;
            if (url) attributs['http.url'] = String(url);

            // Le corps de la réponse porte souvent le message du serveur.
            let detail = '';
            const corps = (error as any).error;
            if (typeof corps === 'string') {
                detail = corps;
            }
            else if (corps && typeof corps === 'object') {
                detail = corps.message || corps.error || '';
            }
            if (!detail) {
                detail = (error as any).message || '';
            }

            const texte = (error as any).statusText ? ` ${(error as any).statusText}` : '';
            return {
                message: `HTTP ${statut}${texte} sur ${url || 'url inconnue'}${detail ? ' — ' + detail : ''}`.slice(0, 1000),
                attributs
            };
        }

        // Objet quelconque : le sérialiser vaut toujours mieux que « [object Object] ».
        try {
            const json = JSON.stringify(error, Object.getOwnPropertyNames(error));
            if (json && json !== '{}') {
                return { message: json.slice(0, 1000), attributs };
            }
        } catch {
            // références circulaires : on retombe sur la description par défaut
        }
        return { message: Object.prototype.toString.call(error), attributs };
    }

    return { message: String(error), attributs };
}

/**
 * Identité de l'utilisateur courant, telle que déposée en localStorage.
 */
export function contexteUtilisateur(): { username?: string; email?: string } {
    try {
        const stable = localStorage.getItem('sentryUser');
        if (stable) {
            try {
                const u = JSON.parse(stable);
                return { username: u?.username || u?.name, email: u?.email };
            } catch {
                return { username: stable };
            }
        }
        const keys = ['msal.active-account', 'username', 'userName', 'user'];
        for (const k of keys) {
            const raw = localStorage.getItem(k);
            if (!raw) continue;
            try {
                const obj = JSON.parse(raw);
                const username = obj?.username || obj?.userName || obj?.name || obj?.displayName || obj?.preferred_username;
                const email = obj?.email || obj?.preferred_username;
                if (username || email) return { username, email };
            } catch {}
        }
    } catch {}
    return {};
}

/**
 * Enregistre une erreur dans OpenTelemetry sans jamais bloquer l'appelant.
 *
 * `attributsSupplementaires` sert à porter le contexte métier — identifiant de la
 * photo, du rapport — sans quoi une erreur remontée du terrain reste inexploitable.
 */
export function enregistrerErreurTelemetrie(
	error: any,
	contexte: string,
	attributsSupplementaires: Record<string, string | number> = {}
): void {
	try {
		const tracer = trace.getTracer('atlas-v2-web');
		const span = tracer.startSpan(`background.error.${contexte}`);

		const utilisateur = contexteUtilisateur();
		if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
		if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);

		const { message, attributs } = decrireErreur(error);
		for (const [cle, valeur] of Object.entries(attributs)) {
			span.setAttribute(cle, valeur);
		}
		for (const [cle, valeur] of Object.entries(attributsSupplementaires)) {
			span.setAttribute(cle, valeur);
		}
		span.setAttribute('error.message', message);
		span.recordException(error instanceof Error ? error : new Error(message));
		span.setAttribute('error.context', contexte);

		span.end();
	} catch (e) {
		console.error('Failed to record error to OTel:', e);
	}
}

/** Au-delà de cette durée, une requête API est remontée en télémétrie. */
export const SEUIL_REQUETE_LENTE_MS = 5000;

/**
 * Remonte une requête API anormalement lente, telle que VUE DU NAVIGATEUR.
 *
 * L'instrumentation HTTP du front est volontairement coupée (volume) au motif que
 * l'API mesure déjà côté serveur. Ça tient tant que la lenteur est dans l'API ;
 * ça ne dit rien du réseau terrain, d'un proxy, ni de ce qu'a réellement attendu
 * l'utilisateur. Le 14/09, une synchro a mis 121 s côté serveur sans qu'aucun
 * signal front ne le dise — le navigateur n'était pas bloqué, il attendait.
 *
 * Un poste sain n'émet rien : seules les requêtes au-delà du seuil remontent.
 */
export function enregistrerRequeteLente(
	methode: string,
	url: string,
	dureeMs: number,
	statut: number | undefined,
	enErreur: boolean
): void {
	try {
		const tracer = trace.getTracer('atlas-v2-web');
		const span = tracer.startSpan('http.slow');
		const utilisateur = contexteUtilisateur();
		if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
		if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
		span.setAttribute('http.method', methode);
		span.setAttribute('http.url', url);
		span.setAttribute('http.duration_ms', Math.round(dureeMs));
		if (statut !== undefined) span.setAttribute('http.status_code', statut);
		span.setAttribute('http.error', enErreur);
		span.end();
	} catch (e) {
		console.error('Failed to record slow request to OTel:', e);
	}
}

/**
 * Remonte un envoi de photos anormal : échec, renvoi photo par photo, ou lot lent.
 * Porte le poids et la durée du lot tels que vus de l'appareil — ce que le serveur ne
 * voit jamais quand la requête n'aboutit pas (« HTTP 0 — Load failed », 02/10).
 */
export function enregistrerEnvoiPhotos(attributs: Record<string, string | number>): void {
	try {
		const span = trace.getTracer('atlas-v2-web').startSpan('photo.envoi_lot');
		const utilisateur = contexteUtilisateur();
		if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
		if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
		for (const [cle, valeur] of Object.entries(attributs)) span.setAttribute(cle, valeur);
		span.end();
	} catch (e) {
		console.error('Failed to record photo upload to OTel:', e);
	}
}

/**
 * Conflit de tableau de dégâts (ATLAS-230) : détection (modification refusée par le serveur,
 * modifié ailleurs) puis choix du technicien (recharger / garder la sienne).
 */
export function enregistrerConflitTableauDegat(attributs: Record<string, string | number>): void {
	try {
		const span = trace.getTracer('atlas-v2-web').startSpan('tableau_degat.conflit');
		const utilisateur = contexteUtilisateur();
		if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
		if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
		for (const [cle, valeur] of Object.entries(attributs)) span.setAttribute(cle, valeur);
		span.end();
	} catch (e) {
		console.error('Failed to record damage table conflict to OTel:', e);
	}
}

/** Au-delà de cette durée, une écriture dans la base locale (IndexedDB) est remontée. */
export const SEUIL_ECRITURE_LOCALE_MS = 300;

/**
 * Remonte une écriture locale anormalement lente, avec le poids de l'enregistrement quand
 * il est connu. Sur iPad, la copie locale d'un gros dossier (100+ photos, 40 phrases) est
 * réécrite à chaque enregistrement du wizard : c'est ce qui figeait la saisie (25/09).
 */
export function enregistrerEcritureLocaleLente(
	operation: string,
	dureeMs: number,
	attributs: Record<string, string | number> = {}
): void {
	try {
		const tracer = trace.getTracer('atlas-v2-web');
		const span = tracer.startSpan('db.local.lente');
		const utilisateur = contexteUtilisateur();
		if (utilisateur.username) span.setAttribute('user.name', utilisateur.username);
		if (utilisateur.email) span.setAttribute('user.email', utilisateur.email);
		span.setAttribute('db.operation', operation);
		span.setAttribute('db.duration_ms', Math.round(dureeMs));
		for (const [cle, valeur] of Object.entries(attributs)) span.setAttribute(cle, valeur);
		span.end();
	} catch (e) {
		console.error('Failed to record slow local write to OTel:', e);
	}
}
